import { useState } from "react";
import {
  categoryEmoji,
  rarityColour,
  type UserAchievement,
} from "@/lib/achievementEngine";
import { FButton } from "./FButton";

const CONFETTI_COLOURS = ["#F97316", "#10B981", "#3B82F6", "#8B5CF6", "#F59E0B"];

export function BadgeReveal({
  badges,
  onDone,
}: {
  badges: UserAchievement[];
  onDone: () => void;
}) {
  const [index, setIndex] = useState(0);
  if (badges.length === 0) return null;
  const current = badges[index];
  const a = current.achievement;
  const rColour = rarityColour(a.rarity);
  const isLast = index === badges.length - 1;
  const isLegendary = a.rarity === "legendary";

  const next = () => {
    if (isLast) onDone();
    else setIndex((i) => i + 1);
  };

  return (
    <div className="absolute inset-0 z-20 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/70 animate-in fade-in duration-300"
        onClick={() => {}}
      />
      <div
        key={index}
        className="relative w-full animate-in slide-in-from-bottom duration-400"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          borderTop: `4px solid ${rColour}`,
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1.25rem)",
          boxShadow: isLegendary
            ? "0 -10px 40px rgba(245,158,11,0.45)"
            : "0 -10px 30px rgba(0,0,0,0.2)",
        }}
      >
        {/* Confetti */}
        <Confetti legendary={isLegendary} key={`c-${index}`} />

        <div className="px-6 pt-8 text-center relative">
          {badges.length > 1 && (
            <div className="text-[12px] tracking-wider text-[var(--colour-text-secondary)] mb-4">
              {index + 1} of {badges.length}
            </div>
          )}

          <div
            className="mx-auto w-28 h-28 rounded-[var(--radius-full)] flex items-center justify-center text-[56px] animate-in zoom-in duration-500"
            style={{
              backgroundColor: rColour + "22",
              border: `3px solid ${rColour}`,
              boxShadow: isLegendary
                ? `0 0 32px ${rColour}`
                : `0 4px 16px ${rColour}33`,
            }}
          >
            {categoryEmoji(a.category)}
          </div>

          <div
            className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em]"
            style={{ color: rColour }}
          >
            {a.rarity}
          </div>

          <h2
            className={
              "mt-2 text-[26px] font-bold " +
              (isLegendary ? "shimmer-text" : "")
            }
          >
            {a.name}
          </h2>

          <p className="mt-2 text-[15px] text-[var(--colour-text-secondary)]">
            {a.description}
          </p>

          <div className="mt-8">
            <FButton fullWidth onClick={next}>
              {isLast ? (badges.length > 1 ? "Done" : "Nice!") : "Next"}
            </FButton>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes confetti-fall {
          0% { transform: translate(0, -20px) rotate(0); opacity: 1; }
          100% { transform: translate(var(--x), 200px) rotate(720deg); opacity: 0; }
        }
        .confetti-dot {
          position: absolute;
          top: 0;
          width: 8px;
          height: 8px;
          border-radius: 2px;
          animation: confetti-fall 1.4s ease-out forwards;
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .shimmer-text {
          background: linear-gradient(90deg, #B8860B 0%, #FFD700 25%, #FFF8DC 50%, #FFD700 75%, #B8860B 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: shimmer 2.4s linear infinite;
        }
      `}</style>
    </div>
  );
}

function Confetti({ legendary }: { legendary: boolean }) {
  const count = legendary ? 40 : 20;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const left = Math.random() * 100;
        const x = (Math.random() - 0.5) * 200;
        const colour = CONFETTI_COLOURS[i % CONFETTI_COLOURS.length];
        const delay = Math.random() * 0.3;
        return (
          <span
            key={i}
            className="confetti-dot"
            style={{
              left: `${left}%`,
              backgroundColor: colour,
              ["--x" as any]: `${x}px`,
              animationDelay: `${delay}s`,
            }}
          />
        );
      })}
    </div>
  );
}
